import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import type { ChartView, PatientChartView } from '@wodi/shared';
import {
  Button,
  CardGrid,
  Cell,
  Column,
  LoadingBlock,
  Notice,
  PageHeader,
  RadioGroup,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
} from '@wodi/ui';
import { useRef, useState } from 'react';

import { chartKeys, issueChart, openChart, patientChartQuery, returnChart } from '../api/charts.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { formatInstant } from '../patients/format.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { toasts } from '../toasts.ts';
import { chartStateTag, MOVEMENT_WORDS, placeWords, returnPeriodWords } from './chart-words.ts';
import { errorText, LoadFailed } from './records-page.tsx';
import styles from './records.module.css';

// A patient's chart at this facility (docs/plans/phase2-medical-records-charts.md §6): its
// number, where it is now, issue and return, and its history. History is never changed: a
// mistaken movement is followed by a correcting one.

const route = getRouteApi('/_app/patients/$patientId/chart');

function useSetView(patientId: string) {
  const queryClient = useQueryClient();
  return (next: PatientChartView) => {
    queryClient.setQueryData(chartKeys.patient(patientId), next);
    void queryClient.invalidateQueries({ queryKey: ['charts', 'list'] });
  };
}

const locationOptions = (view: PatientChartView) =>
  view.options.locations.map((l) => ({ id: l.id, label: `${l.room}, ${l.label}` }));

function NoLocations() {
  return (
    <Notice tone="info">
      No records-room locations are set up at this facility. Add them in Settings &gt; Records.
    </Notice>
  );
}

function OpenChartForm({ view }: { view: PatientChartView }) {
  const online = useOnline();
  const setView = useSetView(view.patientId);
  const requestId = useRef(crypto.randomUUID());
  const [locationId, setLocationId] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      openChart(view.patientId, {
        clientRequestId: requestId.current,
        locationId: locationId ?? '',
      }),
    onSuccess: (next) => {
      setView(next);
      toasts.show({ title: 'Chart opened', description: next.chart?.chartNumber ?? '' });
    },
  });
  if (view.options.locations.length === 0) return <NoLocations />;
  return (
    <form
      className={styles.form}
      aria-label="Open chart"
      onSubmit={(e) => {
        e.preventDefault();
        if (locationId !== null) save.mutate();
      }}
    >
      <Select
        label="File it on"
        placeholder="Choose a location"
        options={locationOptions(view)}
        value={locationId}
        onChange={(id) => {
          setLocationId(typeof id === 'string' ? id : null);
        }}
      />
      {save.error === null ? null : (
        <Notice tone="error">
          {errorText(save.error, 'The chart was not opened. Try again.')}
        </Notice>
      )}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online || locationId === null}
          isPending={save.isPending}
          pendingLabel="Opening…"
        >
          Open chart
        </Button>
      </div>
    </form>
  );
}

function IssueForm({ view, chart }: { view: PatientChartView; chart: ChartView }) {
  const online = useOnline();
  const setView = useSetView(view.patientId);
  const requestId = useRef(crypto.randomUUID());
  const [kind, setKind] = useState<'service_point' | 'ward'>('service_point');
  const [destination, setDestination] = useState<string | null>(null);
  const [handledBy, setHandledBy] = useState('');
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () =>
      issueChart(chart.id, {
        clientRequestId: requestId.current,
        destination: { kind, id: destination ?? '' },
        handledByName: handledBy.trim(),
        reason: reason.trim(),
      }),
    onSuccess: (next) => {
      setView(next);
      requestId.current = crypto.randomUUID();
      setHandledBy('');
      setReason('');
      toasts.show({ title: 'Chart issued', description: chart.chartNumber });
    },
  });
  const choices = kind === 'ward' ? view.options.wards : view.options.servicePoints;
  const ready =
    online &&
    destination !== null &&
    handledBy.trim() !== '' &&
    reason.trim() !== '' &&
    !save.isPending;
  return (
    <form
      className={styles.form}
      aria-label="Issue chart"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) save.mutate();
      }}
    >
      <RadioGroup
        label="Issue to"
        orientation="horizontal"
        value={kind}
        onChange={(v) => {
          setKind(v === 'ward' ? 'ward' : 'service_point');
          setDestination(null);
        }}
        options={[
          { id: 'service_point', label: 'Department' },
          { id: 'ward', label: 'Ward' },
        ]}
      />
      <Select
        label={kind === 'ward' ? 'Ward' : 'Department'}
        placeholder="Choose one"
        options={choices.map((c) => ({ id: c.id, label: c.name }))}
        value={destination}
        onChange={(id) => {
          setDestination(typeof id === 'string' ? id : null);
        }}
      />
      <TextField
        label="Collected by"
        description="The name of the person who took the chart."
        value={handledBy}
        onChange={setHandledBy}
      />
      <TextField
        label="Reason"
        description="For example: clinic appointment, admission, review."
        value={reason}
        onChange={setReason}
      />
      {save.error === null ? null : (
        <Notice tone="error">
          {errorText(save.error, 'The chart was not issued. Try again.')}
        </Notice>
      )}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!ready}
          isPending={save.isPending}
          pendingLabel="Issuing…"
        >
          Issue chart
        </Button>
      </div>
    </form>
  );
}

function ReturnForm({ view, chart }: { view: PatientChartView; chart: ChartView }) {
  const online = useOnline();
  const setView = useSetView(view.patientId);
  const requestId = useRef(crypto.randomUUID());
  const [locationId, setLocationId] = useState<string | null>(null);
  const [handledBy, setHandledBy] = useState('');
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () =>
      returnChart(chart.id, {
        clientRequestId: requestId.current,
        locationId: locationId ?? '',
        handledByName: handledBy.trim() === '' ? null : handledBy.trim(),
        reason: reason.trim() === '' ? null : reason.trim(),
      }),
    onSuccess: (next) => {
      setView(next);
      requestId.current = crypto.randomUUID();
      setHandledBy('');
      setReason('');
      toasts.show({ title: 'Chart returned', description: chart.chartNumber });
    },
  });
  if (view.options.locations.length === 0) return <NoLocations />;
  return (
    <form
      className={styles.form}
      aria-label="Return chart"
      onSubmit={(e) => {
        e.preventDefault();
        if (locationId !== null) save.mutate();
      }}
    >
      <Select
        label="File it on"
        placeholder="Choose a location"
        options={locationOptions(view)}
        value={locationId}
        onChange={(id) => {
          setLocationId(typeof id === 'string' ? id : null);
        }}
      />
      <TextField label="Returned by (optional)" value={handledBy} onChange={setHandledBy} />
      <TextField label="Note (optional)" value={reason} onChange={setReason} />
      {save.error === null ? null : (
        <Notice tone="error">
          {errorText(save.error, 'The chart was not returned. Try again.')}
        </Notice>
      )}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online || locationId === null}
          isPending={save.isPending}
          pendingLabel="Returning…"
        >
          Return chart
        </Button>
      </div>
    </form>
  );
}

function ChartSummary({ view, chart }: { view: PatientChartView; chart: ChartView }) {
  const [word, shape] = chartStateTag(chart.now);
  return (
    <Section title="Chart" actions={<Tag state={shape}>{word}</Tag>}>
      <dl className={styles.facts}>
        <dt>Chart number</dt>
        <dd className="wodi-id">{chart.chartNumber}</dd>
        <dt>{chart.now.state === 'in' ? 'Filed on' : 'With'}</dt>
        <dd>{placeWords(chart.now.place)}</dd>
        <dt>Since</dt>
        <dd>{formatInstant(chart.now.since)}</dd>
        {chart.now.state === 'out' ? (
          <>
            <dt>Due back</dt>
            <dd>
              {chart.now.dueAt === null
                ? returnPeriodWords(view.returnHours)
                : formatInstant(chart.now.dueAt)}
            </dd>
          </>
        ) : null}
        <dt>Opened</dt>
        <dd>{`${formatInstant(chart.openedAt)} by ${chart.openedBy.displayName}`}</dd>
      </dl>
    </Section>
  );
}

function History({ chart }: { chart: ChartView }) {
  return (
    <Section title="History" description="Newest first. Nothing here is changed or deleted.">
      <TableScroll>
        <Table flat aria-label="Chart history">
          <TableHeader>
            <Column isRowHeader>When</Column>
            <Column>Movement</Column>
            <Column>Where</Column>
            <Column>Collected or returned by</Column>
            <Column>Reason</Column>
            <Column>Recorded by</Column>
          </TableHeader>
          <TableBody items={chart.movements}>
            {(m) => (
              <Row id={m.id}>
                <Cell>{formatInstant(m.movedAt)}</Cell>
                <Cell>{MOVEMENT_WORDS[m.kind]}</Cell>
                <Cell>{placeWords(m.place)}</Cell>
                <Cell>{m.handledByName ?? ''}</Cell>
                <Cell>{m.reason ?? ''}</Cell>
                <Cell>{m.recordedBy.displayName}</Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      </TableScroll>
    </Section>
  );
}

function ChartBody({ view, mayManage }: { view: PatientChartView; mayManage: boolean }) {
  const chart = view.chart;
  if (chart === null) {
    return (
      <Section title="Chart" description="No chart at this facility yet.">
        {mayManage ? (
          <OpenChartForm view={view} />
        ) : (
          <p className={styles.meta}>The medical records office opens charts.</p>
        )}
      </Section>
    );
  }
  return (
    <>
      <CardGrid>
        <ChartSummary view={view} chart={chart} />
        {mayManage ? (
          <Section title={chart.now.state === 'in' ? 'Issue chart' : 'Return chart'}>
            {chart.now.state === 'in' ? (
              <IssueForm key={chart.movements.length} view={view} chart={chart} />
            ) : (
              <ReturnForm key={chart.movements.length} view={view} chart={chart} />
            )}
          </Section>
        ) : null}
      </CardGrid>
      <History chart={chart} />
    </>
  );
}

export function PatientChartPage() {
  const { patientId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'records.charts.read');
  const mayManage = session !== null && can(session, 'records.charts.manage');
  const chart = useQuery({ ...patientChartQuery(patientId), enabled: allowed });
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={patientId} />
      <PageHeader
        title="Chart"
        description="The patient's paper file at this facility: where it is and where it has been."
        actions={allowed ? <Link to="/charts">Charts out</Link> : undefined}
      />
      {!allowed ? (
        <Notice tone="info">
          You don&apos;t have permission to see charts. Ask the medical records office.
        </Notice>
      ) : chart.isPending ? (
        <LoadingBlock label="Loading the chart" rows={4} />
      ) : chart.isError ? (
        <LoadFailed
          error={chart.error}
          fallback="The chart couldn't be loaded."
          onRetry={() => void chart.refetch()}
        />
      ) : (
        <ChartBody view={chart.data} mayManage={mayManage} />
      )}
    </div>
  );
}
